import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    VoiceAgentSetupData,
    VoiceAgentSetupDataPatch,
    VoiceAgentSetupDataResponse,
    VoiceAgentAgentConfig,
    VoiceAgentAgentPatch,
    ProvisionNumberResponse,
    VoiceAgentStatusResponse,
} from '../models/VoiceAgent'
import { useUser } from './useUser'

const VOICE_AGENT_KEY = 'voice-agent'
const BASE = '/voice-agent'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        if (typeof data.error === 'string' && data.error) {
            return data.error
        }
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') {
                return val
            }
        }
    }

    return fallback
}

export const useVoiceAgent = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [VOICE_AGENT_KEY] })

    const { data: setupData, isLoading, error, refetch } = useQuery<
        VoiceAgentSetupData,
        AxiosError
    >({
        queryKey: [VOICE_AGENT_KEY, 'setup-data', apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<VoiceAgentSetupData>(
                `${BASE}/setup/data/`,
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        retry: (failureCount, err) => {
            if (err?.response?.status === 404) return false
            return failureCount < 2
        },
    })

    const { data: agentConfig } = useQuery<VoiceAgentAgentConfig, AxiosError>({
        queryKey: [VOICE_AGENT_KEY, 'agent', apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<VoiceAgentAgentConfig>(
                `${BASE}/setup/agent/`,
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnWindowFocus: false,
    })

    const saveSetupData = useMutation<
        VoiceAgentSetupDataResponse,
        AxiosError,
        VoiceAgentSetupDataPatch
    >({
        mutationFn: async (body) => {
            const res = await axiosInstance.patch<VoiceAgentSetupDataResponse>(
                `${BASE}/setup/data/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Property data saved')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to save property data'))
        },
    })

    const saveAgent = useMutation<
        VoiceAgentAgentConfig,
        AxiosError,
        VoiceAgentAgentPatch
    >({
        mutationFn: async (body) => {
            const res = await axiosInstance.patch<VoiceAgentAgentConfig>(
                `${BASE}/setup/agent/`,
                body,
            )
            return res.data
        },
        onSuccess: (_data, variables) => {
            invalidate()
            if (
                variables.enable_service_requests !== undefined &&
                variables.agent_name === undefined &&
                variables.tone === undefined
            ) {
                toast.success(
                    variables.enable_service_requests
                        ? 'Maintenance requests enabled'
                        : 'Maintenance requests disabled',
                )
            } else {
                toast.success('Agent settings saved')
            }
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to save agent settings'))
        },
    })

    const provisionNumber = useMutation<ProvisionNumberResponse, AxiosError>({
        mutationFn: async () => {
            const res = await axiosInstance.post<ProvisionNumberResponse>(
                `${BASE}/setup/number/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
        },
        onError: (err) => {
            const status = err?.response?.status
            if (status === 502) {
                toast.error(
                    "We couldn't provision a number right now. Please try again or contact support.",
                )
            } else {
                toast.error(extractError(err, 'Failed to provision number'))
            }
        },
    })

    const pause = useMutation<VoiceAgentStatusResponse, AxiosError>({
        mutationFn: async () => {
            const res = await axiosInstance.post<VoiceAgentStatusResponse>(
                `${BASE}/pause/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Voice agent paused')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to pause voice agent'))
        },
    })

    const resume = useMutation<VoiceAgentStatusResponse, AxiosError>({
        mutationFn: async () => {
            const res = await axiosInstance.post<VoiceAgentStatusResponse>(
                `${BASE}/resume/`,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Voice agent resumed')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to resume voice agent'))
        },
    })

    return {
        setupData,
        agentConfig,
        isLoading,
        error,
        refetch,
        isApartment,
        saveSetupData,
        saveAgent,
        provisionNumber,
        pause,
        resume,
    }
}
